// [CONTENT WORKSHOP - MAR 2026]: Extracted from App.jsx (was lines 289-363)
// [PURPOSE]: Self-contained component with local state for Race/Class/Subclass creator dialogs
// [WHY]: Keeps dialog state local instead of bloating App or UIContext state
// [INTEGRATION]: Rendered in the Workshop tab (own nav tab since the A11 split;
//   originally Settings), wrapped in PremiumGate by parent
// [CWR-001/CWR-007 FIX — MAY 2026]: Spell Creator button now delegates to App.jsx's
//   single canonical CustomSpellCreator instance via `onOpenSpellCreator` prop. The
//   duplicate mount that lived here (showSpellCreator state) bypassed
//   customComponents wiring + knownSpells integration. Single source of truth: the
//   App.jsx mount around line 12230. Race/Class/Subclass keep local mount because
//   App.jsx has no side-effects on those saves beyond systemBus events.
// [SUB-PLAN 1E — 2026-05-24]: Operations rows for all 7 content types.
//   Monster trio (Stone 9) was the pattern; Item/Feat/Spell trios delegate
//   to App.jsx (same as their creators); Race/Class/Subclass trios are
//   local (creators are local). Each row: Load (custom only) / View
//   (SRD+custom read-only) / Template (fork any as starting point).
import React, { Suspense, lazy, useState } from 'react';
import { Z } from '../constants/zLayers';

const CustomRaceCreator = lazy(() => import('./CustomRaceCreator'));
const CustomClassCreator = lazy(() => import('./CustomClassCreator'));
const CustomSubclassCreator = lazy(() => import('./CustomSubclassCreator'));
// [SUB-PLAN 1E]: Trio drawers + view modals for locally-managed types
const RaceTrioDrawers = lazy(() => import('./RaceTrioDrawers'));
const RaceViewModal = lazy(() => import('./RaceViewModal'));
const ClassTrioDrawers = lazy(() => import('./ClassTrioDrawers'));
const ClassViewModal = lazy(() => import('./ClassViewModal'));
const SubclassTrioDrawers = lazy(() => import('./SubclassTrioDrawers'));
const SubclassViewModal = lazy(() => import('./SubclassViewModal'));
// [SUB-PLAN 3B-followup-trio — 2026-05-31]: Condition trio drawers + view modal
const ConditionTrioDrawers = lazy(() => import('./ConditionTrioDrawers'));
const ConditionViewModal = lazy(() => import('./ConditionViewModal'));
// [CW-BOON-BG-OPERATIONS — 2026-07-11]: Boon + Background trio drawers + view modals.
// Closes the write-only gap: both creators supported templateBoon/templateBackground
// edit-mode since 3A/3C but no surface ever passed a seed back in.
const BoonTrioDrawers = lazy(() => import('./BoonTrioDrawers'));
const BoonViewModal = lazy(() => import('./BoonViewModal'));
const BackgroundTrioDrawers = lazy(() => import('./BackgroundTrioDrawers'));
const BackgroundViewModal = lazy(() => import('./BackgroundViewModal'));

const ContentWorkshopSection = ({
  onOpenSpellCreator,
  onOpenMonsterEditor,
  onOpenItemCreator,
  onOpenFeatCreator,
  // [SUB-PLAN 3B — 2026-05-29]: App.jsx-delegated condition creator (writes playerStats.customConditions)
  onOpenConditionCreator,
  // [SUB-PLAN 3A — 2026-05-30]: App.jsx-delegated boon creator (writes playerStats.customBoons)
  onOpenBoonCreator,
  // [SUB-PLAN 3C — 2026-05-30]: App.jsx-delegated background creator (writes playerStats.customBackgrounds)
  onOpenBackgroundCreator,
  // [CW-DELETE-AFFORDANCE — 2026-07-11]: App.jsx-delegated red-confirm deletes
  // for the trios hosted here whose content lives in playerStats slots.
  onDeleteCondition,
  onDeleteBoon,
  onDeleteBackground,
  // [CW-TRIO-SOURCES — 2026-07-12]: App.jsx-delegated deletes for the trios
  // whose content lives in sourcesManager (in-place removeContentFromSource).
  onDeleteRace,
  onDeleteClass,
  onDeleteSubclass,
  // [STONE 9 / #62 + #81 — 2026-05-18]: View+Edit+Template trio. Workshop
  //   exposes Load (open existing custom in editor), View (read-only
  //   MonsterSheet modal), Template (fork SRD or custom into new draft).
  //   These three callbacks are wired by App.jsx to the MonsterTrioDrawers
  //   + MonsterViewModal mounts. See `src/components/MonsterTrioDrawers.js`.
  onOpenLoadMonster,
  onOpenViewMonster,
  onOpenTemplateMonster,
  // [SUB-PLAN 1E]: Item/Feat/Spell trio callbacks — same pattern as Monster.
  onOpenLoadItem, onOpenViewItem, onOpenTemplateItem,
  onOpenLoadFeat, onOpenViewFeat, onOpenTemplateFeat,
  onOpenLoadSpell, onOpenViewSpell, onOpenTemplateSpell,
  // [SUB-PLAN 1E]: playerStats needed for Race/Class/Subclass local trios
  playerStats,
  logActivity,
  // [BACK-COMPAT — Stone 6 Option A]: Pre-extraction the Workshop button
  //   navigated to the Feats tab. Now there's a proper Workshop-hosted creator
  //   (Stone 6 closure). The prop is preserved for old call sites; new wiring
  //   uses onOpenFeatCreator. If both are passed, onOpenFeatCreator wins.
  onNavigateToFeats,
}) => {
  const [showRaceCreator, setShowRaceCreator] = useState(false);
  const [showClassCreator, setShowClassCreator] = useState(false);
  const [showSubclassCreator, setShowSubclassCreator] = useState(false);
  // [SUB-PLAN 1E]: Template/edit data for local creators
  const [raceTemplate, setRaceTemplate] = useState(null);
  const [raceIsEditing, setRaceIsEditing] = useState(false);
  const [classTemplate, setClassTemplate] = useState(null);
  const [classIsEditing, setClassIsEditing] = useState(false);
  const [subclassTemplate, setSubclassTemplate] = useState(null);
  const [subclassIsEditing, setSubclassIsEditing] = useState(false);
  // [SUB-PLAN 1E]: Local trio drawer + view modal state
  const [loadRaceOpen, setLoadRaceOpen] = useState(false);
  const [viewRaceOpen, setViewRaceOpen] = useState(false);
  const [templateRaceOpen, setTemplateRaceOpen] = useState(false);
  const [viewingRace, setViewingRace] = useState(null);
  const [loadClassOpen, setLoadClassOpen] = useState(false);
  const [viewClassOpen, setViewClassOpen] = useState(false);
  const [templateClassOpen, setTemplateClassOpen] = useState(false);
  const [viewingClass, setViewingClass] = useState(null);
  const [loadSubclassOpen, setLoadSubclassOpen] = useState(false);
  const [viewSubclassOpen, setViewSubclassOpen] = useState(false);
  const [templateSubclassOpen, setTemplateSubclassOpen] = useState(false);
  const [viewingSubclass, setViewingSubclass] = useState(null);
  // [SUB-PLAN 3B-followup-trio — 2026-05-31]: Condition trio drawer + view modal state
  const [loadConditionOpen, setLoadConditionOpen] = useState(false);
  const [viewConditionOpen, setViewConditionOpen] = useState(false);
  const [templateConditionOpen, setTemplateConditionOpen] = useState(false);
  const [viewingCondition, setViewingCondition] = useState(null);
  // [CW-BOON-BG-OPERATIONS — 2026-07-11]: Boon + Background trio drawer + view modal state
  const [loadBoonOpen, setLoadBoonOpen] = useState(false);
  const [viewBoonOpen, setViewBoonOpen] = useState(false);
  const [templateBoonOpen, setTemplateBoonOpen] = useState(false);
  const [viewingBoon, setViewingBoon] = useState(null);
  const [loadBackgroundOpen, setLoadBackgroundOpen] = useState(false);
  const [viewBackgroundOpen, setViewBackgroundOpen] = useState(false);
  const [templateBackgroundOpen, setTemplateBackgroundOpen] = useState(false);
  const [viewingBackground, setViewingBackground] = useState(null);

  return (
    <>
      <div className="bg-gray-800 p-4 rounded-lg mb-4 border border-emerald-600">
        <h3 className="text-lg font-bold mb-3 text-emerald-300 flex items-center gap-2">
          {'\u2692\uFE0F'} Content Workshop
        </h3>
        <p className="text-sm text-gray-400 mb-4">
          Create custom races, classes, subclasses, spells, and monsters for your campaigns. Content is saved to the source of your choice and appears in character creation, the bestiary, and combat manager automatically.
        </p>
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => setShowRaceCreator(true)}
            className="p-3 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center"
          >
            <div className="text-2xl mb-1">{'\uD83E\uDDEC'}</div>
            <div className="font-bold text-white text-sm break-words leading-tight">Race Creator</div>
            <div className="text-xs text-gray-400 mt-1">Custom races & subraces</div>
          </button>
          <button
            onClick={() => setShowClassCreator(true)}
            className="p-3 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center"
          >
            <div className="text-2xl mb-1">{'\u2694\uFE0F'}</div>
            <div className="font-bold text-white text-sm break-words leading-tight">Class Creator</div>
            <div className="text-xs text-gray-400 mt-1">Custom classes & features</div>
          </button>
          <button
            onClick={() => setShowSubclassCreator(true)}
            className="p-3 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center"
          >
            <div className="text-2xl mb-1">{'\uD83C\uDF1F'}</div>
            <div className="font-bold text-white text-sm break-words leading-tight">Subclass Creator</div>
            <div className="text-xs text-gray-400 mt-1">Custom subclass paths</div>
          </button>
          <button
            onClick={() => onOpenSpellCreator && onOpenSpellCreator()}
            disabled={!onOpenSpellCreator}
            className="p-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center"
          >
            <div className="text-2xl mb-1">{'\u2728'}</div>
            <div className="font-bold text-white text-sm break-words leading-tight">Spell Creator</div>
            <div className="text-xs text-gray-400 mt-1">Custom spells & cantrips</div>
          </button>
          {/* [AUDIT 2026-05-17 #48]: Monster Creator moved from Combat Manager
              modal to Workshop. Workshop hosts every authored content type;
              Combat is a pure consumer surface (picks from playerStats.monsterCatalog). */}
          <button
            onClick={() => onOpenMonsterEditor && onOpenMonsterEditor()}
            disabled={!onOpenMonsterEditor}
            className="p-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center"
            data-testid="open-monster-editor"
          >
            <div className="text-2xl mb-1">{'\ud83d\udc32'}</div>
            <div className="font-bold text-white text-sm break-words leading-tight">Monster Creator</div>
            <div className="text-xs text-gray-400 mt-1">Custom monsters & stat blocks</div>
          </button>
          {/* [2026-05-25 items 6+7 refactor]: "Create Item" \u2014 opens the canonical
              ItemDialog (the same comprehensive item authoring surface that lived
              on the Inventory tab pre-refactor). Per user direction, item authoring
              now lives in Workshop alongside the other creators; the Inventory tab
              becomes a pure consumer (find + add already-authored items). The
              earlier half-baked CustomItemCreator parallel was deleted; this
              button delegates to App.jsx's setShowItemDialog. */}
          <button
            onClick={() => onOpenItemCreator && onOpenItemCreator()}
            disabled={!onOpenItemCreator}
            className="p-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center"
            data-testid="open-item-creator"
          >
            <div className="text-2xl mb-1">{'\ud83d\udce6'}</div>
            <div className="font-bold text-white text-sm break-words leading-tight">Create Item</div>
            <div className="text-xs text-gray-400 mt-1">Custom items & magic gear</div>
          </button>
          {/* [STONE 6 CLOSURE / #67 \u2014 2026-05-18]: Feat Creator opens the
              Workshop-hosted CustomFeatCreator directly. Pre-closure this
              navigated to the Feats tab as a stopgap (Option A); Option B
              ships the proper creator. The inline creator inside FeatsPanel
              still works; both write to playerStats.customFeats. */}
          <button
            onClick={() => {
              if (onOpenFeatCreator) onOpenFeatCreator();
              else if (onNavigateToFeats) onNavigateToFeats();
            }}
            disabled={!onOpenFeatCreator && !onNavigateToFeats}
            className="p-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center"
            data-testid="open-feat-creator"
          >
            <div className="text-2xl mb-1">{'\u2b50'}</div>
            <div className="font-bold text-white text-sm break-words leading-tight">Feat Creator</div>
            <div className="text-xs text-gray-400 mt-1">
              {onOpenFeatCreator ? 'Custom feats & abilities' : 'Custom feats (opens Feats tab)'}
            </div>
          </button>
          {/* [SUB-PLAN 3B — 2026-05-29]: Condition Creator — App.jsx-delegated
              (writes playerStats.customConditions via setPlayerStats). Custom
              conditions surface in ConditionsTracker + conditionImmunity pickers
              automatically (buildConditionsMap + composerCatalogRegistry 'condition'). */}
          <button
            onClick={() => onOpenConditionCreator && onOpenConditionCreator()}
            disabled={!onOpenConditionCreator}
            className="p-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center"
            data-testid="open-condition-creator"
          >
            <div className="text-2xl mb-1">{'🌀'}</div>
            <div className="font-bold text-white text-sm break-words leading-tight">Condition Creator</div>
            <div className="text-xs text-gray-400 mt-1">Custom status effects</div>
          </button>
          {/* [SUB-PLAN 3A — 2026-05-30]: Boon Creator — App.jsx-delegated
              (writes playerStats.customBoons via setPlayerStats). Custom boons
              register passiveEffects in effectRegistry automatically via
              useBoonEffects (always-mounted in App.jsx). */}
          <button
            onClick={() => onOpenBoonCreator && onOpenBoonCreator()}
            disabled={!onOpenBoonCreator}
            className="p-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-amber-500 transition-all text-center"
            data-testid="open-boon-creator"
          >
            <div className="text-2xl mb-1">{'⭐'}</div>
            <div className="font-bold text-white text-sm break-words leading-tight">Boon Creator</div>
            <div className="text-xs text-gray-400 mt-1">Epic boons &amp; gifts</div>
          </button>
          {/* [SUB-PLAN 3C — 2026-05-30]: Background Creator — App.jsx-delegated
              (writes playerStats.customBackgrounds via setPlayerStats). Branch A
              narrative-only: saves name, feature, and personality-trait tables.
              Catalog merges with SRD backgrounds via getCatalog('background', playerStats).
              Daggerheart Experiences / CoC Occupations use the same storage shape. */}
          <button
            onClick={() => onOpenBackgroundCreator && onOpenBackgroundCreator()}
            disabled={!onOpenBackgroundCreator}
            className="p-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-blue-500 transition-all text-center"
            data-testid="open-background-creator"
          >
            <div className="text-2xl mb-1">{'📜'}</div>
            <div className="font-bold text-white text-sm break-words leading-tight">Background Creator</div>
            <div className="text-xs text-gray-400 mt-1">Custom backgrounds &amp; features</div>
          </button>
        </div>
        {/* [SUB-PLAN 1E — 2026-05-24]: Operations trios for every content type
            (10 as of CW-BOON-BG-OPERATIONS 2026-07-11).
            Each row: Load (custom only, edit) / View (read-only) / Template (fork).
            Monster (Stone 9 original), then Item/Feat/Spell (App.jsx-delegated),
            then Race/Class/Subclass (locally-managed), then Condition/Boon/
            Background (App.jsx-delegated creators, drawers hosted here). */}
        <div className="mt-4 pt-3 border-t border-gray-700 space-y-3">
          {/* Monster Operations (original Stone 9) */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Monster Operations</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => onOpenLoadMonster && onOpenLoadMonster()} disabled={!onOpenLoadMonster} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-load-monster" title="Pick a custom monster to edit">
                <div className="text-lg mb-0.5">{'📂'}</div>
                <div className="font-bold text-white text-xs">Load</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Edit custom</div>
              </button>
              <button onClick={() => onOpenViewMonster && onOpenViewMonster()} disabled={!onOpenViewMonster} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-view-monster" title="Inspect any monster as a read-only stat block">
                <div className="text-lg mb-0.5">{'👁️'}</div>
                <div className="font-bold text-white text-xs">View</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Read-only</div>
              </button>
              <button onClick={() => onOpenTemplateMonster && onOpenTemplateMonster()} disabled={!onOpenTemplateMonster} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-template-monster" title="Fork an SRD or custom monster as a starting point">
                <div className="text-lg mb-0.5">{'📋'}</div>
                <div className="font-bold text-white text-xs">Template</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Fork any</div>
              </button>
            </div>
          </div>
          {/* Item Operations */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Item Operations</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => onOpenLoadItem && onOpenLoadItem()} disabled={!onOpenLoadItem} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-load-item" title="Pick an inventory item to edit">
                <div className="text-lg mb-0.5">{'📂'}</div>
                <div className="font-bold text-white text-xs">Load</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Edit existing</div>
              </button>
              <button onClick={() => onOpenViewItem && onOpenViewItem()} disabled={!onOpenViewItem} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-view-item" title="Browse the SRD item catalog">
                <div className="text-lg mb-0.5">{'👁️'}</div>
                <div className="font-bold text-white text-xs">View</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Read-only</div>
              </button>
              <button onClick={() => onOpenTemplateItem && onOpenTemplateItem()} disabled={!onOpenTemplateItem} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-template-item" title="Fork an SRD item as a starting point">
                <div className="text-lg mb-0.5">{'📋'}</div>
                <div className="font-bold text-white text-xs">Template</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Fork any</div>
              </button>
            </div>
          </div>
          {/* Feat Operations */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Feat Operations</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => onOpenLoadFeat && onOpenLoadFeat()} disabled={!onOpenLoadFeat} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-load-feat" title="Pick a custom feat to edit">
                <div className="text-lg mb-0.5">{'📂'}</div>
                <div className="font-bold text-white text-xs">Load</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Edit custom</div>
              </button>
              <button onClick={() => onOpenViewFeat && onOpenViewFeat()} disabled={!onOpenViewFeat} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-view-feat" title="Browse all feats read-only">
                <div className="text-lg mb-0.5">{'👁️'}</div>
                <div className="font-bold text-white text-xs">View</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Read-only</div>
              </button>
              <button onClick={() => onOpenTemplateFeat && onOpenTemplateFeat()} disabled={!onOpenTemplateFeat} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-template-feat" title="Fork a feat as a starting point">
                <div className="text-lg mb-0.5">{'📋'}</div>
                <div className="font-bold text-white text-xs">Template</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Fork any</div>
              </button>
            </div>
          </div>
          {/* Spell Operations */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Spell Operations</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => onOpenLoadSpell && onOpenLoadSpell()} disabled={!onOpenLoadSpell} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-load-spell" title="Pick a custom spell to edit">
                <div className="text-lg mb-0.5">{'📂'}</div>
                <div className="font-bold text-white text-xs">Load</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Edit custom</div>
              </button>
              <button onClick={() => onOpenViewSpell && onOpenViewSpell()} disabled={!onOpenViewSpell} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-view-spell" title="Browse all spells read-only">
                <div className="text-lg mb-0.5">{'👁️'}</div>
                <div className="font-bold text-white text-xs">View</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Read-only</div>
              </button>
              <button onClick={() => onOpenTemplateSpell && onOpenTemplateSpell()} disabled={!onOpenTemplateSpell} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-template-spell" title="Fork a spell as a starting point">
                <div className="text-lg mb-0.5">{'📋'}</div>
                <div className="font-bold text-white text-xs">Template</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Fork any</div>
              </button>
            </div>
          </div>
          {/* Race Operations (local — creators are locally mounted) */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Race Operations</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setLoadRaceOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-load-race" title="Pick a custom race to edit">
                <div className="text-lg mb-0.5">{'📂'}</div>
                <div className="font-bold text-white text-xs">Load</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Edit custom</div>
              </button>
              <button onClick={() => setViewRaceOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-view-race" title="Browse all races read-only">
                <div className="text-lg mb-0.5">{'👁️'}</div>
                <div className="font-bold text-white text-xs">View</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Read-only</div>
              </button>
              <button onClick={() => setTemplateRaceOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-template-race" title="Fork a race as a starting point">
                <div className="text-lg mb-0.5">{'📋'}</div>
                <div className="font-bold text-white text-xs">Template</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Fork any</div>
              </button>
            </div>
          </div>
          {/* Class Operations (local) */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Class Operations</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setLoadClassOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-load-class" title="Pick a custom class to edit">
                <div className="text-lg mb-0.5">{'📂'}</div>
                <div className="font-bold text-white text-xs">Load</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Edit custom</div>
              </button>
              <button onClick={() => setViewClassOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-view-class" title="Browse all classes read-only">
                <div className="text-lg mb-0.5">{'👁️'}</div>
                <div className="font-bold text-white text-xs">View</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Read-only</div>
              </button>
              <button onClick={() => setTemplateClassOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-template-class" title="Fork a class as a starting point">
                <div className="text-lg mb-0.5">{'📋'}</div>
                <div className="font-bold text-white text-xs">Template</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Fork any</div>
              </button>
            </div>
          </div>
          {/* Subclass Operations (local) */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Subclass Operations</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setLoadSubclassOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-load-subclass" title="Pick a custom subclass to edit">
                <div className="text-lg mb-0.5">{'📂'}</div>
                <div className="font-bold text-white text-xs">Load</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Edit custom</div>
              </button>
              <button onClick={() => setViewSubclassOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-view-subclass" title="Browse all subclasses read-only">
                <div className="text-lg mb-0.5">{'👁️'}</div>
                <div className="font-bold text-white text-xs">View</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Read-only</div>
              </button>
              <button onClick={() => setTemplateSubclassOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-template-subclass" title="Fork a subclass as a starting point">
                <div className="text-lg mb-0.5">{'📋'}</div>
                <div className="font-bold text-white text-xs">Template</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Fork any</div>
              </button>
            </div>
          </div>
          {/* [SUB-PLAN 3B-followup-trio — 2026-05-31]: Condition Operations (App.jsx-delegated) */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Condition Operations</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setLoadConditionOpen(true)} disabled={!onOpenConditionCreator} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-load-condition" title="Pick a custom condition to edit">
                <div className="text-lg mb-0.5">{'📂'}</div>
                <div className="font-bold text-white text-xs">Load</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Edit custom</div>
              </button>
              <button onClick={() => setViewConditionOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-view-condition" title="Browse all conditions read-only">
                <div className="text-lg mb-0.5">{'👁️'}</div>
                <div className="font-bold text-white text-xs">View</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Read-only</div>
              </button>
              <button onClick={() => setTemplateConditionOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-emerald-500 transition-all text-center" data-testid="open-template-condition" title="Fork a condition as a starting point">
                <div className="text-lg mb-0.5">{'📋'}</div>
                <div className="font-bold text-white text-xs">Template</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Fork any</div>
              </button>
            </div>
          </div>
          {/* [CW-BOON-BG-OPERATIONS — 2026-07-11]: Boon Operations (App.jsx-delegated).
              Boons are custom-only (no SRD catalog) — all three drawers list customBoons. */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Boon Operations</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setLoadBoonOpen(true)} disabled={!onOpenBoonCreator} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-amber-500 transition-all text-center" data-testid="open-load-boon" title="Pick a custom boon to edit">
                <div className="text-lg mb-0.5">{'📂'}</div>
                <div className="font-bold text-white text-xs">Load</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Edit custom</div>
              </button>
              <button onClick={() => setViewBoonOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-amber-500 transition-all text-center" data-testid="open-view-boon" title="Browse your custom boons read-only">
                <div className="text-lg mb-0.5">{'👁️'}</div>
                <div className="font-bold text-white text-xs">View</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Read-only</div>
              </button>
              <button onClick={() => setTemplateBoonOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-amber-500 transition-all text-center" data-testid="open-template-boon" title="Fork a boon as a starting point">
                <div className="text-lg mb-0.5">{'📋'}</div>
                <div className="font-bold text-white text-xs">Template</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Fork any</div>
              </button>
            </div>
          </div>
          {/* [CW-BOON-BG-OPERATIONS — 2026-07-11]: Background Operations (App.jsx-delegated).
              Load = custom only; View/Template = SRD_BACKGROUNDS + custom merged. */}
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">Background Operations</p>
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => setLoadBackgroundOpen(true)} disabled={!onOpenBackgroundCreator} className="p-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg border border-gray-600 hover:border-blue-500 transition-all text-center" data-testid="open-load-background" title="Pick a custom background to edit">
                <div className="text-lg mb-0.5">{'📂'}</div>
                <div className="font-bold text-white text-xs">Load</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Edit custom</div>
              </button>
              <button onClick={() => setViewBackgroundOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-blue-500 transition-all text-center" data-testid="open-view-background" title="Browse all backgrounds read-only">
                <div className="text-lg mb-0.5">{'👁️'}</div>
                <div className="font-bold text-white text-xs">View</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Read-only</div>
              </button>
              <button onClick={() => setTemplateBackgroundOpen(true)} className="p-2 bg-gray-700 hover:bg-gray-600 rounded-lg border border-gray-600 hover:border-blue-500 transition-all text-center" data-testid="open-template-background" title="Fork a background as a starting point">
                <div className="text-lg mb-0.5">{'📋'}</div>
                <div className="font-bold text-white text-xs">Template</div>
                <div className="text-[10px] text-gray-400 mt-0.5">Fork any</div>
              </button>
            </div>
          </div>
        </div>
        <div className="mt-3 bg-emerald-900/20 border border-emerald-700/50 rounded p-3">
          <div className="text-xs text-emerald-200">
            <strong>Tip:</strong> Created content appears in Character Creation, Level Up, and the Spells tab. Use the Content Sources section above to enable or disable sourcebooks before creating content.
          </div>
        </div>
      </div>

      {/* Creator Dialogs — now accept templateX + isEditing for trio Load/Template flow */}
      {showRaceCreator && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/80 flex items-center justify-center" style={{ zIndex: Z.DROPDOWN }}><div className="text-white">Loading Race Creator...</div></div>}>
          <CustomRaceCreator
            onClose={() => { setShowRaceCreator(false); setRaceTemplate(null); setRaceIsEditing(false); }}
            onSave={() => { setShowRaceCreator(false); setRaceTemplate(null); setRaceIsEditing(false); }}
            templateRace={raceTemplate}
            isEditing={raceIsEditing}
          />
        </Suspense>
      )}
      {showClassCreator && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/80 flex items-center justify-center" style={{ zIndex: Z.DROPDOWN }}><div className="text-white">Loading Class Creator...</div></div>}>
          <CustomClassCreator
            onClose={() => { setShowClassCreator(false); setClassTemplate(null); setClassIsEditing(false); }}
            onSave={() => { setShowClassCreator(false); setClassTemplate(null); setClassIsEditing(false); }}
            templateClass={classTemplate}
            isEditing={classIsEditing}
            playerStats={playerStats}
          />
        </Suspense>
      )}
      {showSubclassCreator && (
        <Suspense fallback={<div className="fixed inset-0 bg-black/80 flex items-center justify-center" style={{ zIndex: Z.DROPDOWN }}><div className="text-white">Loading Subclass Creator...</div></div>}>
          <CustomSubclassCreator
            onClose={() => { setShowSubclassCreator(false); setSubclassTemplate(null); setSubclassIsEditing(false); }}
            onSave={() => { setShowSubclassCreator(false); setSubclassTemplate(null); setSubclassIsEditing(false); }}
            templateSubclass={subclassTemplate}
            isEditing={subclassIsEditing}
          />
        </Suspense>
      )}
      {/* Spell Creator delegates to App.jsx canonical mount via onOpenSpellCreator. */}

      {/* [SUB-PLAN 1E]: Local trio drawers for Race/Class/Subclass */}
      {(loadRaceOpen || viewRaceOpen || templateRaceOpen) && (
        <Suspense fallback={null}>
          <RaceTrioDrawers
            playerStats={playerStats}
            onDelete={onDeleteRace}
            loadOpen={loadRaceOpen}
            viewOpen={viewRaceOpen}
            templateOpen={templateRaceOpen}
            onCloseLoad={() => setLoadRaceOpen(false)}
            onCloseView={() => setViewRaceOpen(false)}
            onCloseTemplate={() => setTemplateRaceOpen(false)}
            onPickEdit={(race) => {
              setLoadRaceOpen(false);
              setRaceTemplate(race); setRaceIsEditing(true); setShowRaceCreator(true);
            }}
            onPickTemplate={(cloned) => {
              setTemplateRaceOpen(false);
              setRaceTemplate(cloned); setRaceIsEditing(false); setShowRaceCreator(true);
            }}
            onPickView={(race) => { setViewRaceOpen(false); setViewingRace(race); }}
            logActivity={logActivity}
          />
        </Suspense>
      )}
      {viewingRace && (
        <Suspense fallback={null}>
          <RaceViewModal
            race={viewingRace}
            onClose={() => setViewingRace(null)}
            onForkToTemplate={(cloned) => {
              setViewingRace(null);
              setRaceTemplate(cloned); setRaceIsEditing(false); setShowRaceCreator(true);
            }}
            logActivity={logActivity}
          />
        </Suspense>
      )}
      {(loadClassOpen || viewClassOpen || templateClassOpen) && (
        <Suspense fallback={null}>
          <ClassTrioDrawers
            playerStats={playerStats}
            onDelete={onDeleteClass}
            loadOpen={loadClassOpen}
            viewOpen={viewClassOpen}
            templateOpen={templateClassOpen}
            onCloseLoad={() => setLoadClassOpen(false)}
            onCloseView={() => setViewClassOpen(false)}
            onCloseTemplate={() => setTemplateClassOpen(false)}
            onPickEdit={(cls) => {
              setLoadClassOpen(false);
              setClassTemplate(cls); setClassIsEditing(true); setShowClassCreator(true);
            }}
            onPickTemplate={(cloned) => {
              setTemplateClassOpen(false);
              setClassTemplate(cloned); setClassIsEditing(false); setShowClassCreator(true);
            }}
            onPickView={(cls) => { setViewClassOpen(false); setViewingClass(cls); }}
            logActivity={logActivity}
          />
        </Suspense>
      )}
      {viewingClass && (
        <Suspense fallback={null}>
          <ClassViewModal
            cls={viewingClass}
            onClose={() => setViewingClass(null)}
            onForkToTemplate={(cloned) => {
              setViewingClass(null);
              setClassTemplate(cloned); setClassIsEditing(false); setShowClassCreator(true);
            }}
            logActivity={logActivity}
          />
        </Suspense>
      )}
      {(loadSubclassOpen || viewSubclassOpen || templateSubclassOpen) && (
        <Suspense fallback={null}>
          <SubclassTrioDrawers
            playerStats={playerStats}
            onDelete={onDeleteSubclass}
            loadOpen={loadSubclassOpen}
            viewOpen={viewSubclassOpen}
            templateOpen={templateSubclassOpen}
            onCloseLoad={() => setLoadSubclassOpen(false)}
            onCloseView={() => setViewSubclassOpen(false)}
            onCloseTemplate={() => setTemplateSubclassOpen(false)}
            onPickEdit={(sc) => {
              setLoadSubclassOpen(false);
              setSubclassTemplate(sc); setSubclassIsEditing(true); setShowSubclassCreator(true);
            }}
            onPickTemplate={(cloned) => {
              setTemplateSubclassOpen(false);
              setSubclassTemplate(cloned); setSubclassIsEditing(false); setShowSubclassCreator(true);
            }}
            onPickView={(sc) => { setViewSubclassOpen(false); setViewingSubclass(sc); }}
            logActivity={logActivity}
          />
        </Suspense>
      )}
      {viewingSubclass && (
        <Suspense fallback={null}>
          <SubclassViewModal
            subclass={viewingSubclass}
            onClose={() => setViewingSubclass(null)}
            onForkToTemplate={(cloned) => {
              setViewingSubclass(null);
              setSubclassTemplate(cloned); setSubclassIsEditing(false); setShowSubclassCreator(true);
            }}
            logActivity={logActivity}
          />
        </Suspense>
      )}
      {/* [SUB-PLAN 3B-followup-trio — 2026-05-31]: Condition trio drawers (App.jsx-delegated) */}
      {(loadConditionOpen || viewConditionOpen || templateConditionOpen) && (
        <Suspense fallback={null}>
          <ConditionTrioDrawers
            playerStats={playerStats}
            loadOpen={loadConditionOpen}
            viewOpen={viewConditionOpen}
            templateOpen={templateConditionOpen}
            onCloseLoad={() => setLoadConditionOpen(false)}
            onCloseView={() => setViewConditionOpen(false)}
            onCloseTemplate={() => setTemplateConditionOpen(false)}
            onPickEdit={(cond) => {
              setLoadConditionOpen(false);
              if (onOpenConditionCreator) onOpenConditionCreator(cond);
            }}
            onPickTemplate={(cloned) => {
              setTemplateConditionOpen(false);
              if (onOpenConditionCreator) onOpenConditionCreator(cloned);
            }}
            onPickView={(cond) => { setViewConditionOpen(false); setViewingCondition(cond); }}
            onDelete={onDeleteCondition}
            logActivity={logActivity}
          />
        </Suspense>
      )}
      {viewingCondition && (
        <Suspense fallback={null}>
          <ConditionViewModal
            condition={viewingCondition}
            onClose={() => setViewingCondition(null)}
            onForkToTemplate={(cloned) => {
              setViewingCondition(null);
              if (onOpenConditionCreator) onOpenConditionCreator(cloned);
            }}
            logActivity={logActivity}
          />
        </Suspense>
      )}
      {/* [CW-BOON-BG-OPERATIONS — 2026-07-11]: Boon trio drawers (App.jsx-delegated) */}
      {(loadBoonOpen || viewBoonOpen || templateBoonOpen) && (
        <Suspense fallback={null}>
          <BoonTrioDrawers
            playerStats={playerStats}
            loadOpen={loadBoonOpen}
            viewOpen={viewBoonOpen}
            templateOpen={templateBoonOpen}
            onCloseLoad={() => setLoadBoonOpen(false)}
            onCloseView={() => setViewBoonOpen(false)}
            onCloseTemplate={() => setTemplateBoonOpen(false)}
            onPickEdit={(boon) => {
              setLoadBoonOpen(false);
              if (onOpenBoonCreator) onOpenBoonCreator(boon);
            }}
            onPickTemplate={(cloned) => {
              setTemplateBoonOpen(false);
              if (onOpenBoonCreator) onOpenBoonCreator(cloned);
            }}
            onPickView={(boon) => { setViewBoonOpen(false); setViewingBoon(boon); }}
            onDelete={onDeleteBoon}
            logActivity={logActivity}
          />
        </Suspense>
      )}
      {viewingBoon && (
        <Suspense fallback={null}>
          <BoonViewModal
            boon={viewingBoon}
            onClose={() => setViewingBoon(null)}
            onForkToTemplate={(cloned) => {
              setViewingBoon(null);
              if (onOpenBoonCreator) onOpenBoonCreator(cloned);
            }}
            logActivity={logActivity}
          />
        </Suspense>
      )}
      {/* [CW-BOON-BG-OPERATIONS — 2026-07-11]: Background trio drawers (App.jsx-delegated) */}
      {(loadBackgroundOpen || viewBackgroundOpen || templateBackgroundOpen) && (
        <Suspense fallback={null}>
          <BackgroundTrioDrawers
            playerStats={playerStats}
            loadOpen={loadBackgroundOpen}
            viewOpen={viewBackgroundOpen}
            templateOpen={templateBackgroundOpen}
            onCloseLoad={() => setLoadBackgroundOpen(false)}
            onCloseView={() => setViewBackgroundOpen(false)}
            onCloseTemplate={() => setTemplateBackgroundOpen(false)}
            onPickEdit={(bg) => {
              setLoadBackgroundOpen(false);
              if (onOpenBackgroundCreator) onOpenBackgroundCreator(bg);
            }}
            onPickTemplate={(cloned) => {
              setTemplateBackgroundOpen(false);
              if (onOpenBackgroundCreator) onOpenBackgroundCreator(cloned);
            }}
            onPickView={(bg) => { setViewBackgroundOpen(false); setViewingBackground(bg); }}
            onDelete={onDeleteBackground}
            logActivity={logActivity}
          />
        </Suspense>
      )}
      {viewingBackground && (
        <Suspense fallback={null}>
          <BackgroundViewModal
            background={viewingBackground}
            onClose={() => setViewingBackground(null)}
            onForkToTemplate={(cloned) => {
              setViewingBackground(null);
              if (onOpenBackgroundCreator) onOpenBackgroundCreator(cloned);
            }}
            logActivity={logActivity}
          />
        </Suspense>
      )}
    </>
  );
};

export default ContentWorkshopSection;
